import React, { CSSProperties } from 'react'
import { Typography } from '@mui/material'
import { CssStyles } from '../../types'
import { cx } from '@emotion/css'
import { getClassName, parseClassNames } from '../../utils'

export interface IText {
  /** Text to be displayed.
   *
   */
  text?: string

  /** The component used for the root node.
   *
   */
  component?: string

  /** Set the text-align on the component.
   *
   * @default inherit
   */
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify'

  /** The color of the component. It supports those
   * theme colors that make sense for this component.
   *
   * @default initial
   */
  color?:
    | 'initial'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'textPrimary'
    | 'textSecondary'
    | 'error'

  /** Controls the display type
   *
   * @default initial
   */
  display?: 'initial' | 'block' | 'inline'

  /** If true, the text will have a bottom margin.
   *
   * @default false
   */
  gutterBottom?: boolean

  /** If true, the text will not wrap, but instead
   * will truncate with a text overflow ellipsis.
   * Note that text overflow can only happen with block or
   * inline-block level elements (the element needs to have a
   * width in order to overflow).
   *
   * @default false
   */
  noWrap?: boolean

  /** If true, the text will have a bottom margin.
   *
   * @default false
   */
  paragraph?: boolean

  /** Applies the theme typography styles.
   *
   * @default body1
   */
  variant?:
    | 'body1'
    | 'body2'
    | 'button'
    | 'caption'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'inherit'
    | 'overline'
    | 'subtitle1'
    | 'subtitle2'

  /** *Deprecated* Raw CSS styles
   * @deprecated
   */
  styles?: CssStyles<'root'>

  style?: CSSProperties | string
}

export const Text = React.forwardRef<HTMLParagraphElement, IText>(
  ({ text, styles, style, ...rest }, ref) => {
    const classNames = styles && parseClassNames(styles)

    return (
      <Typography
        ref={ref}
        classes={{ root: cx(classNames?.root, getClassName(style)) }}
        {...rest}
      >
        {text}
      </Typography>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  Text.displayName = 'Text'
}
